import { friendlyDate } from '../api/teacher'
import { today } from '../api/calendar'
import { useSession } from '../useSession'

/**
 * Says so, loudly, whenever the app is pretending it is another day (D-386).
 *
 * <b>The worst failure this feature has is a silent one</b>: a tester looks at a
 * screen on a simulated date, finds something odd, and reports it as a bug
 * against today. That costs an investigation and some trust, and it is very easy
 * to do — the whole point of the override is that the app looks completely
 * normal afterwards.
 *
 * <b>So the banner is not dismissible and does not scroll away.</b> A control to
 * hide it would be used once and then forgotten, which returns the app to
 * exactly the state this exists to prevent. Clearing the date is how it goes,
 * and the banner says so.
 *
 * <b>It reads the session rather than asking.</b> The first version fetched
 * <c>/api/dev/clock</c> on every screen — which is a <b>404 wherever the
 * override is off</b>, so every signed-in page on production logged a console
 * error, and the layout audit checks console errors on every route. Caught by
 * driving the deployed site straight after shipping it. The session is fetched
 * once and cached and every screen behind a login already has it, so a host
 * that is not simulating anything now costs no request at all.
 */
export default function SimulatedDateBanner() {
  const { session } = useSession()

  const simulated = session?.simulatedDate
  if (!simulated) return null

  return (
    <div
      role="status"
      className="flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 bg-warn-bg px-4 py-1.5 text-center text-[12.5px] font-semibold text-warn"
    >
      <span>Showing {friendlyDate(simulated, today()).replace(/^on /, '')}, not today.</span>
      <span className="font-normal opacity-80">
        The clock is set forward for testing &mdash; anything dated is simulated.
      </span>
    </div>
  )
}
